import React from 'react';

const TermsOfUse: React.FC = () => {
  return (
    <div className="text-sm text-gray-300 space-y-6">
      <h1 className="text-2xl font-bold text-white mb-6">Privacy Policy</h1>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Solvrays Privacy Policy</h2>
        <p className="text-sm leading-relaxed text-gray-300"><strong>Effective Date:</strong> February 9, 2025</p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Introduction and Scope</h2>
        <p className="text-sm leading-relaxed text-gray-300">
          This Privacy Policy describes the privacy practices of Solvrays LLC and its services. When we refer to “Solvrays,” “we,” or “our” in this policy, we are referring to Solvrays LLC and its platform. The Solvrays platform is accessed as a web application via a computer’s browser and as a mobile application on mobile devices.
        </p>
        <p className="text-sm leading-relaxed text-gray-300">This Privacy Policy is intended to help you understand:</p>
        <ul className="list-disc ml-6 space-y-2">
          <li>The information we collect</li>
          <li>How we use it</li>
          <li>Who we share it with</li>
          <li>The choices you have regarding your data</li>
        </ul>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Consent Acknowledgment</h2>
        <p className="text-sm leading-relaxed text-gray-300">
          By using the Solvrays platform, you consent to the collection, use, and processing of your information as described in this Privacy Policy. If you do not agree with any part of this policy, please do not use the platform.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Children’s Privacy</h2>
        <p className="text-sm leading-relaxed text-gray-300">
          The Solvrays platform is not directed toward or intended for use by children. Users under the age of 18 (or the equivalent minimum age in the relevant jurisdiction) are not permitted to create accounts or use the platform.
        </p>
        <p className="text-sm leading-relaxed text-gray-300">
          If a parent or guardian believes that a child has provided personal information to Solvrays, they may contact us at <a href="mailto:contact@solvrays.com" className="text-blue-500 hover:underline">contact@solvrays.com</a>, and we will take appropriate steps to delete such information.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Information We Collect</h2>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Information You Provide Us</h3>
          <p className="text-sm leading-relaxed text-gray-300"><strong>a) Account Information:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Name</li>
            <li>Email address</li>
            <li>Password</li>
            <li>Mobile phone number</li>
            <li>Company name (if applicable)</li>
            <li>State/country of residence</li>
            <li>Other relevant details required for account setup</li>
          </ul>

          <p className="text-sm leading-relaxed text-gray-300"><strong>b) Additional Information You Provide:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Contacting support or submitting inquiries</li>
            <li>Participating in surveys, polls, contests, or promotions</li>
            <li>Engaging with customer feedback or research</li>
          </ul>

          <p className="text-sm leading-relaxed text-gray-300"><strong>c) Third-Party Account Connections:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Name</li>
            <li>Profile picture</li>
            <li>Email address</li>
            <li>Other relevant details shared by the third-party service</li>
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Information We Receive from Your Use of Our Platform</h2>

        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-gray-300"><strong>a) Device and Usage Information:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>IP addresses</li>
            <li>Browser type and language</li>
            <li>Operating system and device type</li>
            <li>Mobile device identifiers</li>
            <li>Pages visited and features used within the platform</li>
            <li>Date, time, and duration of activities</li>
          </ul>

          <p className="text-sm leading-relaxed text-gray-300"><strong>b) Location Information:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Precise location data (GPS signals, Wi-Fi access points, or cell tower IDs)</li>
            <li>Approximate location derived from your IP address</li>
          </ul>
          <p className="text-sm leading-relaxed text-gray-300">You can disable location tracking at any time through your device settings.</p>

          <p className="text-sm leading-relaxed text-gray-300"><strong>c) Interaction Data:</strong></p>
          <ul className="list-disc ml-6 space-y-2">
            <li>Search queries</li>
            <li>Clicks on content and resources</li>
            <li>Time spent on different sections</li>
            <li>Interaction with customer support or automated features</li>
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">Information Collected via Cookies and Similar Technologies</h2>
        <p className="text-sm leading-relaxed text-gray-300">
          We use cookies and tracking technologies to:
        </p>
        <ul className="list-disc ml-6 space-y-2">
          <li>Improve platform performance and security</li>
          <li>Store user preferences and session data</li>
          <li>Analyze user behavior for insights and improvements</li>
        </ul>
        <p className="text-sm leading-relaxed text-gray-300">
          Most web browsers allow users to control or disable cookies through settings. However, disabling cookies may affect the functionality of the Solvrays platform.
        </p>
      </section>

      <section>
        <h2 className="text-xl font-semibold text-white mb-4">How to Contact Us</h2>
        <p className="text-sm leading-relaxed text-gray-300">
          <strong>Email:</strong> <a href="mailto:contact@solvrays.com" className="text-blue-500 hover:underline">contact@solvrays.com</a>
        </p>
        <p className="text-sm leading-relaxed text-gray-300">
          <strong>Address:</strong> 500 West 5th Street, Winston-Salem, NC 27101, USA
        </p>
      </section>
    </div>
  );
};

export default TermsOfUse;
